import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, TextField,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import AppLayout from '../../SideBar';
import HeadingComponent from '../../../components/ui/HeadingComponent/index.js';
import strings from '../../../constant/strings';
import { LoadingContainer, NoDetailsContainer } from '../../../assets/styles/CommonStyles';
import { getReturnableItemsApi, getShopOrdersApi, requestShopReturnApi } from '../../../services/shopService';
import { RETURN_REASON_OPTIONS, formatDate, formatMoney, returnLabel, statusLabel } from './utils';
import {
  BodyText, CartLine, DeliveryStep, DeliverySteps, HeaderRow, InfoBanner, LineInfo,
  MutedText, OutlineButton, PageContainer, SectionCard, SectionTitle, SummaryRow, Thumb,
} from './styles';

/**
 * Orders live in Shopify. The backend reads them for the signed-in patient and
 * this page lists them; nothing about orders is stored in our database.
 */
const MyOrdersContent = () => {
  const history = useHistory();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The return the patient is filling in, if any.
  const [returnFor, setReturnFor] = useState(null);
  const [returnReason, setReturnReason] = useState('DEFECTIVE');
  const [returnNote, setReturnNote] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  // Which lines of the order the patient wants to send back.
  const [returnItems, setReturnItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [picked, setPicked] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [matchedEmail, setMatchedEmail] = useState('');
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));

  // Shopify's thank-you page links back here with ?checkout=success
  const checkoutSuccess = new URLSearchParams(location.search).get('checkout') === 'success';

  // Reads the first page. Also used by Refresh and after a return, so the list
  // never shows a status the patient has already changed.
  const load = () => {
    setLoading(true);
    setError(null);
    return getShopOrdersApi({ token: userData.token })
      .then((res) => {
        if (res.response_type === 'success') {
          const data = res.response.data || {};
          setOrders(data.orders || []);
          setReason(data.reason || null);
          setNextCursor(data.nextCursor || null);
          setMatchedEmail(data.email || '');
        } else {
          setError((res.response && res.response.message) || strings.shopUnavailable);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => load();

  // Older orders are fetched a page at a time and appended.
  const loadMore = () => {
    setLoadingMore(true);
    getShopOrdersApi({ token: userData.token, cursor: nextCursor })
      .then((res) => {
        if (res.response_type === 'success') {
          const data = res.response.data || {};
          setOrders((current) => [...current, ...(data.orders || [])]);
          setNextCursor(data.nextCursor || null);
        }
      })
      .catch(() => { /* keep what is already on screen */ })
      .finally(() => setLoadingMore(false));
  };

  // Shopify only takes a return on something that actually shipped, and never
  // on a cancelled order or one that is already being returned.
  const canReturn = (order) => !order.cancelledAt && !order.returnStatus
    && (order.deliveryStatus === 'delivered' || order.fulfillmentStatus === 'fulfilled');

  const openReturn = (order) => {
    setReturnFor(order);
    setNotice('');
    setReturnItems([]);
    setPicked({});
    setItemsLoading(true);
    getReturnableItemsApi({ token: userData.token, orderName: order.name })
      .then((res) => {
        if (res.response_type === 'success') {
          const items = (res.response.data || {}).items || [];
          setReturnItems(items);
          // Everything selected by default: the common case is the whole order.
          setPicked(items.reduce((all, item) => ({ ...all, [item.id]: item.quantity }), {}));
        }
      })
      .catch(() => { /* the patient can still send the request for everything */ })
      .finally(() => setItemsLoading(false));
  };

  const togglePicked = (item) => setPicked((current) => {
    const next = { ...current };
    if (next[item.id]) delete next[item.id]; else next[item.id] = item.quantity;
    return next;
  });

  const setPickedQty = (item, quantity) =>
    setPicked((current) => ({ ...current, [item.id]: Number(quantity) }));

  const sendReturn = () => {
    const items = Object.keys(picked).map((id) => ({ id, quantity: picked[id] }));
    if (returnItems.length && !items.length) {
      setNotice(strings.returnSelectOne);
      return;
    }
    setSending(true);
    requestShopReturnApi({
      token: userData.token,
      orderName: returnFor.name,
      reason: returnReason,
      note: returnNote,
      items: items.length ? items : undefined,
    })
      .then((res) => {
        if (res.response_type === 'success') {
          setNotice(strings.returnSuccess);
          setReturnFor(null);
          setReturnNote('');
          return refresh();
        }
        setNotice((res.response && res.response.message) || strings.returnFailed);
        return null;
      })
      .catch(() => setNotice(strings.returnFailed))
      .finally(() => setSending(false));
  };

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <HeadingComponent text={{ primary: strings.myOrders, secondary: strings.myOrdersHeadingText }} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <OutlineButton onClick={refresh} disabled={loading}>{strings.refresh}</OutlineButton>
          <OutlineButton onClick={() => history.push('/patient/shop')}>{strings.backToShop}</OutlineButton>
        </div>
      </HeaderRow>

      {checkoutSuccess ? <InfoBanner style={{ marginTop: 16 }}>{strings.orderPlacedSuccess}</InfoBanner> : null}
      {notice ? <InfoBanner style={{ marginTop: 16 }}>{notice}</InfoBanner> : null}

      {loading ? <LoadingContainer>{strings.loading}</LoadingContainer> : null}

      {!loading && error ? (
        <NoDetailsContainer style={{ flexDirection: 'column', gap: 12 }}>
          <MutedText>{error}</MutedText>
          <OutlineButton onClick={refresh}>{strings.tryAgain}</OutlineButton>
        </NoDetailsContainer>
      ) : null}

      {!loading && !error && orders.length > 0 && matchedEmail ? (
        <MutedText style={{ marginTop: 12 }}>
          {strings.showingOrdersFor} {matchedEmail}
        </MutedText>
      ) : null}

      {!loading && !error && orders.length === 0 ? (
        <NoDetailsContainer style={{ flexDirection: 'column', gap: 8 }}>
          {reason === 'not_configured' ? <MutedText>{strings.shopUnavailable}</MutedText> : null}
          {reason === 'no_email' ? <MutedText>{strings.ordersNeedEmail}</MutedText> : null}
          {!reason ? (
            <>
              <MutedText>{strings.noOrdersAvailable}</MutedText>
              <MutedText>{strings.ordersMatchedByEmail}</MutedText>
            </>
          ) : null}
        </NoDetailsContainer>
      ) : null}

      {!loading && !error && orders.map((order) => {
        // A cancelled order keeps its payment state (refunded, voided) but has
        // no journey left, so the shipping chips and the step line are replaced
        // by the cancellation itself.
        const isCancelled = Boolean(order.cancelledAt);
        return (
        <SectionCard key={order.name} elevation={0} style={{ marginTop: 16 }}>
          <HeaderRow style={{ alignItems: 'center' }}>
            <div>
              <SectionTitle style={{ marginBottom: 2 }}>{order.name}</SectionTitle>
              <MutedText>{formatDate(order.createdAt)}</MutedText>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={statusLabel(order.financialStatus)} sx={{ fontFamily: 'Poppins', fontSize: 12 }} />
              {isCancelled ? (
                <Chip size="small" color="error" label={strings.orderCancelled} sx={{ fontFamily: 'Poppins', fontSize: 12 }} />
              ) : (
                <>
                  <Chip size="small" label={statusLabel(order.fulfillmentStatus)} sx={{ fontFamily: 'Poppins', fontSize: 12 }} />
                  {order.deliveryStatus ? (
                    <Chip
                      size="small"
                      color={order.deliveryStatus === 'delivered' ? 'success' : 'default'}
                      label={statusLabel(order.deliveryStatus)}
                      sx={{ fontFamily: 'Poppins', fontSize: 12 }}
                    />
                  ) : null}
                </>
              )}
              {order.returnStatus ? (
                <Chip size="small" color="warning" label={returnLabel(order.returnStatus)} sx={{ fontFamily: 'Poppins', fontSize: 12 }} />
              ) : null}
              {canReturn(order) ? (
                <OutlineButton size="small" onClick={() => openReturn(order)}>
                  {strings.requestReturn}
                </OutlineButton>
              ) : null}
              {order.statusPageUrl ? (
                <OutlineButton size="small" href={order.statusPageUrl} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon />}>
                  {strings.viewOrder}
                </OutlineButton>
              ) : null}
            </div>
          </HeaderRow>

          {isCancelled ? (
            <MutedText style={{ marginTop: 8 }}>
              {strings.orderCancelledOn} {formatDate(order.cancelledAt)}
              {order.cancelReason ? ` · ${statusLabel(order.cancelReason)}` : ''}
            </MutedText>
          ) : (
          <>
          {/* Ordered -> shipped -> delivered, with the dates Shopify reports */}
          <DeliverySteps>
            {[
              { label: strings.stepOrdered, at: order.createdAt, done: true },
              { label: strings.stepShipped, at: order.shippedAt, done: Boolean(order.shippedAt) },
              { label: strings.stepDelivered, at: order.deliveredAt, done: Boolean(order.deliveredAt) },
            ].map((step, index, steps) => (
              <DeliveryStep key={step.label} data-done={step.done ? 'true' : 'false'}>
                <span className="dot" />
                <span className="label">{step.label}</span>
                <span className="when">{step.at ? formatDate(step.at) : '—'}</span>
                {index < steps.length - 1 ? <span className="bar" /> : null}
              </DeliveryStep>
            ))}
            {!order.deliveredAt && order.estimatedDelivery ? (
              <MutedText>{strings.estimatedDelivery}: {formatDate(order.estimatedDelivery)}</MutedText>
            ) : null}
          </DeliverySteps>
          {order.deliveryStatus === 'shipment_cancelled' ? (
            <MutedText style={{ marginTop: 8 }}>{strings.shipmentStopped}</MutedText>
          ) : null}
          </>
          )}

          {(order.items || []).map((item, index) => (
            <CartLine key={`${order.name}-${index}`}>
              <Thumb>
                {item.imageUrl ? <img src={item.imageUrl} alt={item.title} /> : <ShoppingBagOutlinedIcon sx={{ color: '#cfcfd7' }} />}
              </Thumb>
              <LineInfo>
                <BodyText>{item.title}</BodyText>
                {item.variantTitle ? <MutedText>{item.variantTitle}</MutedText> : null}
              </LineInfo>
              <MutedText>× {item.quantity}</MutedText>
            </CartLine>
          ))}

          {(order.tracking || []).map((t) => (
            <SummaryRow key={t.number || t.url}>
              <span>{[t.company, t.number].filter(Boolean).join(' · ')}</span>
              {t.url ? (
                <OutlineButton size="small" href={t.url} target="_blank" rel="noopener noreferrer">{strings.trackShipment}</OutlineButton>
              ) : null}
            </SummaryRow>
          ))}

          <SummaryRow className="total">
            <span>{strings.total}</span>
            <span>{formatMoney(order.totalPrice, order.currency)}</span>
          </SummaryRow>
        </SectionCard>
        );
      })}

      {!loading && !error && nextCursor ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <OutlineButton onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? strings.loading : strings.loadMore}
          </OutlineButton>
        </div>
      ) : null}

      <Dialog open={Boolean(returnFor)} onClose={() => setReturnFor(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: 'Poppins', fontSize: 16, fontWeight: 600 }}>
          {strings.returnDialogTitle} {returnFor ? returnFor.name : ''}
        </DialogTitle>
        <DialogContent>
          <MutedText style={{ marginBottom: 16 }}>{strings.returnDialogText}</MutedText>

          {itemsLoading ? <MutedText>{strings.returnItemsLoading}</MutedText> : null}
          {!itemsLoading && returnItems.length === 0 ? (
            <MutedText>{strings.returnNothingLeft}</MutedText>
          ) : null}
          {/* One line per returnable item. A single-item order is pre-ticked,
              so the common case is unchanged: open, pick a reason, send. */}
          {!itemsLoading && returnItems.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <BodyText style={{ marginBottom: 8 }}>{strings.returnItemsTitle}</BodyText>
              {returnItems.map((item) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(picked[item.id])}
                        onChange={() => togglePicked(item)}
                      />
                    )}
                    label={`${item.title}${item.variantTitle ? ` · ${item.variantTitle}` : ''}`}
                    sx={{ flex: 1, '& .MuiFormControlLabel-label': { fontFamily: 'Poppins', fontSize: 13 } }}
                  />
                  {item.quantity > 1 && picked[item.id] ? (
                    <TextField
                      select
                      size="small"
                      value={picked[item.id]}
                      onChange={(event) => setPickedQty(item, event.target.value)}
                      sx={{ width: 72 }}
                    >
                      {Array.from({ length: item.quantity }, (unused, index) => index + 1).map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <TextField
            select
            fullWidth
            size="small"
            label={strings.returnReason}
            value={returnReason}
            onChange={(event) => setReturnReason(event.target.value)}
          >
            {RETURN_REASON_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            style={{ marginTop: 16 }}
            label={strings.returnNote}
            value={returnNote}
            onChange={(event) => setReturnNote(event.target.value.slice(0, 255))}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 20px' }}>
          <Button onClick={() => setReturnFor(null)} sx={{ fontFamily: 'Poppins', textTransform: 'none' }}>
            {strings.cancel}
          </Button>
          <OutlineButton
            size="small"
            onClick={sendReturn}
            disabled={sending || itemsLoading || (!itemsLoading && returnItems.length === 0)}
          >
            {sending ? strings.loading : strings.returnSubmit}
          </OutlineButton>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

const MyOrders = () => (
  <Grid container spacing={0}>
    <AppLayout>
      <MyOrdersContent />
    </AppLayout>
  </Grid>
);

export default MyOrders;
